import {getChat, getComments, sendChat, postComment, requestSeek, resolveComment, editComment, deleteComment} from './api';
import type {ChatVisit} from './chat-visit';
import {mergeMessages, selectMessages, capturePosition, type Message, type MessageFilter, type Position, type Draft} from './message-feed';

interface DiscussionDeps {
  session: string; token: () => string | undefined; hostToken: () => string | undefined;
  host: boolean; clientId: string; author: () => string; visit: ChatVisit; chatRoot?: HTMLElement;
  position: () => Position; formatPosition: (seconds: number) => string;
  canMark: () => boolean;
  onState: (open: boolean, canWrite: boolean, canComment: boolean) => void;
}

/** One composer; timestamped messages retain the established comment/REAPER protocol. */
export class Discussion {
  private chat = document.createElement('section');
  private list = document.createElement('ol');
  private input = document.createElement('textarea');
  private send = document.createElement('button');
  private mark = document.createElement('button');
  private anchor = document.createElement('button');
  private notice = document.createElement('p');
  private error = document.createElement('p');
  private failed = document.createElement('div');
  private newMessages = document.createElement('button');
  private filters: HTMLButtonElement[] = [];
  private filter: MessageFilter = 'all';
  private frozen?: Position;
  private failedDrafts: Draft[] = [];
  private archiveExpanded = false;
  private messages: Message[] = [];
  private lastKey = '';
  private lastId = '';
  private unread = 0;
  private open = false;
  private canWrite = false;
  private canComment = false;
  private busy = false;
  private disposed = false;
  private polling = false;
  private refreshAgain = false;
  private revision = 0;
  private editing?: string;
  private timer: number;

  constructor(private root: HTMLElement, private deps: DiscussionDeps) {
    root.classList.add('discussion', 'discussion--unified');
    const heading = document.createElement('h2'); heading.textContent = 'Messages';
    this.chat.className = 'discussion__chat'; this.chat.setAttribute('aria-label','Messages');
    const header = document.createElement('header'); header.className = 'discussion__header'; header.append(heading);
    if (deps.host) {
      const filters = document.createElement('div'); filters.className = 'discussion__filters'; filters.setAttribute('aria-label','Message filters');
      for (const [value,label] of [['all','All messages'],['position','With timecode'],['open','Open items']] as const) {
        const button = this.button(label, () => { this.filter=value; this.lastKey=''; this.renderMessages(); this.paint(); });
        button.dataset.filter=value; filters.append(button); this.filters.push(button);
      }
      header.append(filters);
    }
    this.list.className='discussion__messages'; this.list.setAttribute('aria-label','Messages');
    this.list.onscroll=()=>{ if(this.atBottom() && this.unread){this.unread=0;this.paint();} };
    this.newMessages.type='button'; this.newMessages.className='discussion__new';
    this.newMessages.onclick=()=>{this.filter='all';this.lastKey='';this.renderMessages();this.list.scrollTop=this.list.scrollHeight;this.unread=0;this.paint();};
    this.failed.className='discussion__failed-list';
    this.notice.className='control-room__muted'; this.error.setAttribute('role','alert');
    this.input.rows=2;this.input.maxLength=500;this.input.placeholder='Write a message…';this.input.setAttribute('aria-label','Message');
    this.input.title='Enter to send · Shift+Enter for a new line · Alt/Option+Enter to mark position';
    this.input.addEventListener('keydown',e=>{
      if(e.key==='Enter' && e.altKey && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.isComposing && e.keyCode!==229){e.preventDefault();if(!e.repeat)this.mark.click();return;}
      if(e.key==='Enter' && !e.shiftKey && !e.isComposing && e.keyCode!==229 && !e.ctrlKey && !e.altKey && !e.metaKey){e.preventDefault();void this.submit();}
    });
    this.mark.type='button';this.mark.textContent='Mark position';this.mark.className='btn btn--ghost';
    this.mark.title='Mark position · Alt+Enter (Windows) / Option+Enter (Mac)';this.mark.setAttribute('aria-keyshortcuts','Alt+Enter');
    this.mark.onclick=()=>{this.frozen=capturePosition(this.deps.position());this.input.focus({preventScroll:true});this.paint();};
    this.anchor.type='button';this.anchor.className='discussion__anchor';
    this.anchor.onclick=()=>{this.frozen=undefined;this.input.focus({preventScroll:true});this.paint();};
    this.send.type='submit';this.send.textContent='Send';this.send.className='btn btn--primary';
    for(const button of [this.mark,this.anchor,this.send]) button.addEventListener('pointerdown',e=>{if(document.activeElement===this.input)e.preventDefault();});
    const actions=document.createElement('div');actions.className='discussion__compose-actions';actions.append(this.mark,this.send);
    const form=document.createElement('form');form.className='discussion__compose';form.append(this.anchor,this.input,actions);
    form.onsubmit=e=>{e.preventDefault();void this.submit();};
    this.chat.append(header,this.notice,this.list,this.newMessages,form,this.failed,this.error);
    if(deps.chatRoot){deps.chatRoot.append(this.chat);root.hidden=true;}else root.append(this.chat);
    this.paint();void this.refresh();this.timer=window.setInterval(()=>void this.refresh(),2000);
  }
  private button(label:string, action:()=>void){const b=document.createElement('button');b.type='button';b.textContent=label;b.onclick=action;return b;}
  setState(open:boolean, canWrite:boolean){this.open=open;this.canWrite=canWrite;this.paint();}
  invalidate(){this.revision++;void this.refresh();}
  private paint(){
    if(!this.deps.chatRoot)this.root.hidden=!this.deps.host&&!this.open&&!this.lastId;
    this.notice.hidden=this.open;
    this.notice.textContent=this.deps.host?'Open communication to enable messages.':'Communication is closed. You can keep listening.';
    this.input.disabled=!this.open||(!this.canWrite&&!this.canComment);
    this.send.disabled=!this.open||this.busy||(this.frozen ? !this.canComment||!this.deps.canMark() : !this.canWrite);
    this.mark.disabled=!this.open||!this.canComment||!this.deps.canMark();
    this.mark.textContent=this.frozen?'Update position':'Mark position';
    this.anchor.hidden=!this.frozen;
    this.anchor.textContent=this.frozen?`${this.frozen.label} · Remove timecode`:'';
    this.anchor.setAttribute('aria-label',this.frozen?`Remove timecode ${this.frozen.label}`:'Remove timecode');
    this.newMessages.hidden=!this.unread;this.newMessages.textContent=`${this.unread} new messages`;
    for(const button of this.filters)button.setAttribute('aria-pressed',String(button.dataset.filter===this.filter));
  }
  private async refresh(){
    const token=this.deps.token();if(!token||this.disposed)return;
    if(this.polling){this.refreshAgain=true;return;}
    this.polling=true;const revision=this.revision;
    try{
      const [snapshot,positions]=await Promise.all([getChat(this.deps.session,token),getComments(this.deps.session,{token:this.deps.hostToken()??token,clientId:this.deps.clientId})]);
      if(this.disposed||revision!==this.revision){this.refreshAgain=true;return;}
      this.open=snapshot.open;this.canWrite=snapshot.canWrite;this.canComment=snapshot.canComment??snapshot.canWrite;
      this.deps.onState(this.open,this.canWrite,this.canComment);
      this.messages=mergeMessages(snapshot.messages,positions);this.renderMessages();this.paint();
    }catch{/* Keep the current view and drafts on transient network errors. */}
    finally{this.polling=false;if(this.refreshAgain&&!this.disposed){this.refreshAgain=false;void this.refresh();}}
  }
  private atBottom(){return this.list.scrollHeight-this.list.scrollTop-this.list.clientHeight<40;}
  private renderMessages(){
    if(this.editing)return;
    const {visible,archiveCount}=selectMessages(this.messages,this.deps.visit,this.filter,this.archiveExpanded);
    const key=JSON.stringify([this.filter,this.archiveExpanded,this.messages]);if(key===this.lastKey)return;
    const bottom=this.atBottom(), top=this.list.scrollTop, newest=this.messages.at(-1)?.id??'';
    if(this.lastKey&&newest!==this.lastId&&!bottom){const previous=this.messages.findIndex(m=>m.id===this.lastId);this.unread+=previous>=0?this.messages.length-previous-1:1;}
    this.lastId=newest;this.lastKey=key;this.list.replaceChildren();
    if(archiveCount&&this.filter==='all'){
      const row=document.createElement('li');const toggle=this.button(`${this.archiveExpanded?'Hide':'Show'} ${archiveCount} earlier chat ${archiveCount===1?'message':'messages'}`,()=>{
        const height=this.list.scrollHeight,scroll=this.list.scrollTop;this.archiveExpanded=!this.archiveExpanded;this.lastKey='';this.renderMessages();this.list.scrollTop=Math.max(0,scroll+this.list.scrollHeight-height);
      });toggle.className='discussion__archive-toggle';toggle.setAttribute('aria-expanded',String(this.archiveExpanded));row.append(toggle);this.list.append(row);
    }
    for(const message of visible)this.list.append(this.messageRow(message));
    if(!visible.length){const empty=document.createElement('li');empty.className='discussion__empty';empty.textContent=this.filter==='open'?'No open items.':this.filter==='position'?'No messages with timecode.':'No new messages yet.';this.list.append(empty);}
    this.list.scrollTop=bottom?this.list.scrollHeight:top;if(bottom)this.unread=0;
  }
  private messageRow(message:Message){
    const row=document.createElement('li'),own=!!message.clientId&&message.clientId===this.deps.clientId;
    row.className=`discussion__message${own?' discussion__message--own':''}`;
    if(message.color&&/^#[0-9a-f]{6}$/i.test(message.color))row.style.setProperty('--chat-author-color',message.color);
    const author=document.createElement('strong');author.textContent=message.author;
    if(own){const you=document.createElement('span');you.className='discussion__you';you.textContent='You';author.append(you);}
    const time=document.createElement('time');time.dateTime=new Date(message.ts).toISOString();time.textContent=new Date(message.ts).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});time.title=new Date(message.ts).toLocaleString('en-GB');
    const header=document.createElement('div');header.className='discussion__message-header';header.append(author,time);
    const body=document.createElement('p');body.textContent=message.text;row.append(header);
    if(message.kind==='position'){
      const meta=document.createElement('div');meta.className='discussion__position';
      const hostToken=this.deps.hostToken();
      const tc=this.button(this.deps.formatPosition(message.projectSec),()=>{if(hostToken)void requestSeek(this.deps.session,message.projectSec,hostToken).catch(e=>this.showError(e));});
      tc.className='discussion__timecode';tc.disabled=!hostToken;tc.title=hostToken?'Jump to position in the DAW':'DAW position';meta.append(tc);
      if(message.resolved){const done=document.createElement('span');done.textContent='Resolved';done.className='discussion__you';meta.append(done);}
      header.append(meta);row.append(body);
      if(hostToken||own){
        const actions=document.createElement('div');actions.className='discussion__message-actions';
        if(hostToken)actions.append(this.button(message.resolved?'Reopen':'Resolve',()=>void this.mutate(()=>resolveComment(this.deps.session,message.id,!message.resolved,hostToken))));
        actions.append(this.button('Edit',()=>this.edit(message,row,body)));
        const remove=this.button('Delete',()=>{if(remove.dataset.armed!=='true'){remove.dataset.armed='true';remove.textContent='Confirm delete';return;}void this.mutate(()=>deleteComment(this.deps.session,message.id,this.auth()));});actions.append(remove);row.append(actions);
      }
    }else row.append(body);
    return row;
  }
  private auth(){return{clientId:this.deps.clientId,listenerToken:this.deps.token(),hostToken:this.deps.hostToken()};}
  private edit(message:Message,row:HTMLElement,body:HTMLElement){
    if(this.editing)return;
    this.editing=message.id;const input=document.createElement('textarea');input.value=message.text;input.maxLength=500;input.setAttribute('aria-label','Edit message');
    const form=document.createElement('form');form.className='discussion__edit';
    const save=document.createElement('button');save.type='submit';save.textContent='Save';
    const cancel=this.button('Cancel',()=>{this.editing=undefined;this.lastKey='';this.renderMessages();});
    form.append(input,save,cancel);body.replaceWith(form);row.querySelector('.discussion__message-actions')?.remove();input.focus();
    form.onsubmit=e=>{e.preventDefault();if(!input.value.trim())return;save.disabled=true;cancel.disabled=true;void editComment(this.deps.session,message.id,input.value.trim(),this.auth()).then(()=>{this.editing=undefined;this.lastKey="";this.invalidate();}).catch(e=>{this.showError(e);save.disabled=false;cancel.disabled=false;});};
  }
  private showError(e:unknown){this.error.textContent=e instanceof Error?e.message:'Could not complete the action. Please retry.';}
  private async mutate(action:()=>Promise<unknown>){try{await action();this.invalidate();}catch(e){this.showError(e);}}
  private async deliver(draft:Draft){
    const token=this.deps.token();if(!token)throw new Error('Please reconnect before sending.');
    if(draft.position)await postComment(this.deps.session,{text:draft.text,projectSec:draft.position.projectSec,author:this.deps.author(),clientId:this.deps.clientId},token);
    else await sendChat(this.deps.session,token,draft.text);
  }
  private renderFailed(){
    this.failed.replaceChildren();for(const draft of this.failedDrafts){
      const row=document.createElement('div');row.className='discussion__failed';const text=document.createElement('p');text.textContent=`Not sent${draft.position?` · ${draft.position.label}`:''}: ${draft.text}`;
      const retry=this.button('Retry',()=>{retry.disabled=true;void this.deliver(draft).then(()=>{this.failedDrafts=this.failedDrafts.filter(d=>d!==draft);this.error.textContent='';this.renderFailed();this.invalidate();}).catch(e=>{this.showError(e);retry.disabled=false;});});row.append(text,retry);this.failed.append(row);
    }
  }
  private async submit(){
    const text=this.input.value.trim();if(!text||this.busy||this.send.disabled)return;
    this.input.focus({preventScroll:true});const draft:Draft={text,position:this.frozen};this.frozen=undefined;this.input.value='';
    this.busy=true;this.error.textContent='';this.paint();
    try{await this.deliver(draft);this.filter='all';this.invalidate();this.unread=0;}
    catch(e){this.failedDrafts.push(draft);this.renderFailed();this.showError(e);}
    finally{this.busy=false;this.paint();}
  }
  destroy(){this.disposed=true;window.clearInterval(this.timer);}
}
